import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// ========================================
// Grid de Clases reutilizable
// ========================================
const ClaseGrid = ({ clases, user, onClaseClick, color, actionLabel, actionIcon }) => {
  const isAdmin = user?.rol === 'admin';
  const isProfesor = user?.rol === 'profesor';

  if (clases.length === 0) {
    return <EmptyState icon="fas fa-graduation-cap" title="No hay clases disponibles" subtitle="No hay clases activas disponibles." />;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {clases.map((clase) => (
        <Card key={clase.id} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group cursor-pointer" onClick={() => onClaseClick(clase)}>
          <div className="relative h-40 bg-gradient-to-br from-blue-500 to-indigo-600 overflow-hidden">
            {clase.banner_url ? (
              <img src={clase.banner_url} alt={clase.titulo} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <i className="fas fa-graduation-cap text-5xl text-white/50"></i>
              </div>
            )}
            {isAdmin && (
              <div className={`absolute top-2 right-2 px-2 py-1 rounded text-xs font-medium ${clase.categoria === 'profesores' ? 'bg-purple-600 text-white' : 'bg-blue-600 text-white'}`}>
                {clase.categoria === 'profesores' ? 'Capacitacion' : 'Estudiantes'}
              </div>
            )}
            <div className={`absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300`}>
              <div className={`w-16 h-16 rounded-full ${color === 'red' ? 'bg-red-600' : color === 'emerald' ? 'bg-emerald-600' : 'bg-purple-600'} flex items-center justify-center`}>
                <i className={`${actionIcon} text-white text-2xl`}></i>
              </div>
            </div>
          </div>
          <CardContent className="p-4">
            <h3 className="font-bold text-lg text-gray-800 mb-2 line-clamp-2">{clase.titulo}</h3>
            {clase.profesor_nombre && (
              <div className="flex items-center gap-2 mb-4 text-sm text-gray-600">
                <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                  {clase.profesor_foto ? (
                    <img src={clase.profesor_foto} alt={clase.profesor_nombre} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400"><i className="fas fa-user"></i></div>
                  )}
                </div>
                <span className="truncate">{clase.profesor_nombre}</span>
              </div>
            )}
            <Button className={`w-full ${color === 'red' ? 'bg-red-600 hover:bg-red-700' : color === 'emerald' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-purple-600 hover:bg-purple-700'} text-white`}>
              <i className={`${actionIcon} mr-2`}></i> {actionLabel}
            </Button>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// ========================================
// Panel de contenido multimedia de una clase
// ========================================
const MediaPanel = ({ clase, tipo, user, onBack }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ titulo: '', descripcion: '', url: '', autor: '', duracion: '' });
  const [saving, setSaving] = useState(false);

  const isAdmin = user?.rol === 'admin';
  const isProfesor = user?.rol === 'profesor';
  const canManage = isAdmin || isProfesor;

  const label = tipo === 'audiolibro' ? 'Audio Libro' : 'Podcast';
  const icon = tipo === 'audiolibro' ? 'fas fa-headphones' : 'fas fa-podcast';

  const cargarItems = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/clases/${clase.id}/media-content?tipo=${tipo}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(res.data.items || []);
    } catch (err) {
      toast.error(`Error al cargar ${label}s`);
    } finally {
      setLoading(false);
    }
  }, [clase.id, tipo, label]);

  useEffect(() => { cargarItems(); }, [cargarItems]);

  const resetForm = () => {
    setForm({ titulo: '', descripcion: '', url: '', autor: '', duracion: '' });
    setEditItem(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.titulo || !form.url) {
      toast.error('Titulo y URL son requeridos');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      if (editItem) {
        await axios.put(`${API}/media-content/${editItem.id}`, form, { headers });
        toast.success(`${label} actualizado`);
      } else {
        await axios.post(`${API}/clases/${clase.id}/media-content`, { ...form, tipo }, { headers });
        toast.success(`${label} agregado a ${clase.titulo}`);
      }
      setShowDialog(false);
      resetForm();
      cargarItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm(`Eliminar este ${label}?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/media-content/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${label} eliminado`);
      cargarItems();
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  return (
    <div className="space-y-4">
      {/* Header con boton volver */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <button onClick={onBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors" data-testid="media-back-btn">
          <i className="fas fa-arrow-left"></i>
          <span className="text-sm font-medium">Volver a clases</span>
        </button>
        {canManage && (
          <Button onClick={() => { resetForm(); setShowDialog(true); }} className={`${tipo === 'audiolibro' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-purple-600 hover:bg-purple-700'} text-white`} data-testid={`crear-${tipo}-btn`}>
            <i className="fas fa-plus mr-2"></i> Agregar {label}
          </Button>
        )}
      </div>

      {/* Clase seleccionada */}
      <div className={`rounded-xl p-4 ${tipo === 'audiolibro' ? 'bg-emerald-50 border border-emerald-200' : 'bg-purple-50 border border-purple-200'}`}>
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${tipo === 'audiolibro' ? 'bg-emerald-600' : 'bg-purple-600'} text-white`}>
            <i className={`${icon} text-xl`}></i>
          </div>
          <div>
            <h2 className="font-bold text-gray-800">{clase.titulo}</h2>
            <p className={`text-sm ${tipo === 'audiolibro' ? 'text-emerald-600' : 'text-purple-600'}`}>
              {items.length} {label}{items.length !== 1 ? 's' : ''} disponible{items.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      </div>

      {/* Lista de items */}
      {loading ? (
        <LoadingSpinner text={`Cargando ${label}s...`} />
      ) : items.length === 0 ? (
        <EmptyState icon={icon} title={`No hay ${label}s`} subtitle={canManage ? `Haz clic en "Agregar ${label}" para comenzar.` : `Aun no se han agregado ${label}s para esta clase.`} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item) => (
            <Card key={item.id} className="border-0 shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden" data-testid={`media-card-${item.id}`}>
              <div className={`h-3 ${tipo === 'audiolibro' ? 'bg-gradient-to-r from-emerald-500 to-teal-500' : 'bg-gradient-to-r from-purple-500 to-pink-500'}`}></div>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${tipo === 'audiolibro' ? 'bg-emerald-100 text-emerald-600' : 'bg-purple-100 text-purple-600'}`}>
                      <i className={`${icon} text-lg`}></i>
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-gray-800 text-sm line-clamp-2">{item.titulo}</h3>
                      {item.autor && <p className="text-xs text-gray-500 truncate">{item.autor}</p>}
                    </div>
                  </div>
                  {canManage && (
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={() => { setForm({ titulo: item.titulo || '', descripcion: item.descripcion || '', url: item.url || '', autor: item.autor || '', duracion: item.duracion || '' }); setEditItem(item); setShowDialog(true); }} className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors" data-testid={`edit-${item.id}`}>
                        <i className="fas fa-pen text-xs"></i>
                      </button>
                      {isAdmin && (
                        <button onClick={() => handleDelete(item.id)} className="p-1.5 text-gray-400 hover:text-red-600 transition-colors" data-testid={`delete-${item.id}`}>
                          <i className="fas fa-trash text-xs"></i>
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {item.descripcion && <p className="text-xs text-gray-600 line-clamp-2">{item.descripcion}</p>}
                <div className="flex items-center justify-between">
                  {item.duracion && (
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                      <i className="fas fa-clock"></i> {item.duracion}
                    </span>
                  )}
                  <a href={item.url} target="_blank" rel="noopener noreferrer"
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white transition-colors ${tipo === 'audiolibro' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-purple-600 hover:bg-purple-700'}`}
                    data-testid={`play-${item.id}`}>
                    <i className={tipo === 'podcast' ? 'fas fa-play' : 'fas fa-headphones'}></i>
                    {tipo === 'podcast' ? 'Escuchar' : 'Abrir'}
                  </a>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog crear/editar */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <i className={`${icon} ${tipo === 'audiolibro' ? 'text-emerald-600' : 'text-purple-600'}`}></i>
              {editItem ? `Editar ${label}` : `Agregar ${label}`}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <Label>Titulo *</Label>
              <Input value={form.titulo} onChange={(e) => setForm({...form, titulo: e.target.value})} placeholder={tipo === 'audiolibro' ? 'Ej: La Biblia en Audio - Genesis' : 'Ej: Episodio 1 - Fe y Vida'} required data-testid="media-titulo-input" />
            </div>
            <div>
              <Label>URL del {tipo === 'podcast' ? 'MP3 o enlace' : 'enlace'} *</Label>
              <Input value={form.url} onChange={(e) => setForm({...form, url: e.target.value})} placeholder={tipo === 'podcast' ? 'https://ejemplo.com/episodio.mp3' : 'https://ejemplo.com/audiolibro'} required data-testid="media-url-input" />
            </div>
            <div>
              <Label>Autor / Narrador</Label>
              <Input value={form.autor} onChange={(e) => setForm({...form, autor: e.target.value})} placeholder="Nombre del autor o narrador" data-testid="media-autor-input" />
            </div>
            <div>
              <Label>Duracion</Label>
              <Input value={form.duracion} onChange={(e) => setForm({...form, duracion: e.target.value})} placeholder="Ej: 45 min, 1h 30min" data-testid="media-duracion-input" />
            </div>
            <div>
              <Label>Descripcion</Label>
              <textarea value={form.descripcion} onChange={(e) => setForm({...form, descripcion: e.target.value})} placeholder="Breve descripcion del contenido..." className="w-full border rounded-lg p-2 text-sm min-h-[80px] resize-none" data-testid="media-desc-input" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="submit" disabled={saving} className={`flex-1 ${tipo === 'audiolibro' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-purple-600 hover:bg-purple-700'} text-white`} data-testid="media-save-btn">
                {saving ? <><i className="fas fa-spinner fa-spin mr-2"></i>Guardando...</> : <><i className="fas fa-save mr-2"></i>{editItem ? 'Actualizar' : 'Guardar'}</>}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ========================================
// Componentes auxiliares
// ========================================
const LoadingSpinner = ({ text }) => (
  <div className="flex items-center justify-center min-h-[300px]">
    <div className="text-center">
      <i className="fas fa-spinner fa-spin text-4xl text-blue-600 mb-4"></i>
      <p className="text-gray-600">{text}</p>
    </div>
  </div>
);

const EmptyState = ({ icon, title, subtitle }) => (
  <Card className="border-0 shadow-lg">
    <CardContent className="p-8 text-center">
      <i className={`${icon} text-6xl text-gray-300 mb-4`}></i>
      <h3 className="text-xl font-semibold text-gray-700 mb-2">{title}</h3>
      <p className="text-gray-500">{subtitle}</p>
    </CardContent>
  </Card>
);

// ========================================
// Componente Principal
// ========================================
const VideosClases = () => {
  const [clases, setClases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [selectedClase, setSelectedClase] = useState(null);
  const [selectedTipo, setSelectedTipo] = useState(null);
  const [registrando, setRegistrando] = useState(null);

  useEffect(() => {
    const userDataStr = localStorage.getItem('cached_user');
    if (userDataStr) {
      try {
        const parsedUser = JSON.parse(userDataStr);
        setUser(parsedUser);
        cargarClases(parsedUser);
      } catch (e) {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  const cargarClases = async (currentUser) => {
    if (!currentUser) { setLoading(false); return; }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/clases`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const userRol = currentUser?.rol;
      const userId = currentUser?.id;
      let clasesFiltered = [];

      if (userRol === 'admin') {
        clasesFiltered = response.data.filter(clase => clase.activa);
      } else if (userRol === 'profesor') {
        // Profesor ve cualquier clase donde dicte o esté inscrito (incluye devocionales)
        clasesFiltered = response.data.filter(clase => {
          if (!clase.activa) return false;
          const esProfesor = clase.profesor_id === userId;
          const estaInscrito = clase.estudiantes_inscritos?.includes(userId);
          return esProfesor || estaInscrito;
        });
      } else {
        // Estudiante/pastor/graduado: cualquier clase activa donde esté inscrito,
        // excepto las exclusivas para profesores
        clasesFiltered = response.data.filter(clase =>
          clase.activa
          && clase.categoria !== 'profesores'
          && clase.estudiantes_inscritos?.includes(userId)
        );
      }
      setClases(clasesFiltered);
    } catch (error) {
      toast.error('Error al cargar las clases');
    } finally {
      setLoading(false);
    }
  };

  const isAdmin = user?.rol === 'admin';
  const isProfesor = user?.rol === 'profesor';

  const handleVerVideos = async (clase) => {
    window.location.href = `/app/clases/${clase.id}/videos`;
  };

  const handleMediaClaseClick = (clase, tipo) => {
    setSelectedClase(clase);
    setSelectedTipo(tipo);
  };

  const handleBackToClases = () => {
    setSelectedClase(null);
    setSelectedTipo(null);
  };

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => window.history.length > 2 ? window.history.back() : (window.location.href = '/app/dashboard')}
        className="text-gray-600 hover:text-gray-800"
        data-testid="back-button"
      >
        <i className="fas fa-arrow-left mr-2"></i>
        Volver
      </Button>

      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-xl p-6 text-white shadow-lg">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-white/20 rounded-xl">
            <i className="fas fa-play-circle text-3xl"></i>
          </div>
          <div>
            <h1 className="text-2xl font-bold">Mediateca</h1>
            <p className="text-red-100">Videos de clases, audio libros y podcasts</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="videos" className="w-full" onValueChange={() => handleBackToClases()}>
        <TabsList className="w-full grid grid-cols-3 h-12 bg-gray-100 rounded-xl p-1" data-testid="media-tabs">
          <TabsTrigger value="videos" className="rounded-lg data-[state=active]:bg-red-600 data-[state=active]:text-white text-sm font-medium" data-testid="tab-videos">
            <i className="fab fa-youtube mr-2"></i>
            <span className="hidden sm:inline">Videos de Clases</span>
            <span className="sm:hidden">Videos</span>
          </TabsTrigger>
          <TabsTrigger value="audiolibros" className="rounded-lg data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-sm font-medium" data-testid="tab-audiolibros">
            <i className="fas fa-headphones mr-2"></i>
            <span className="hidden sm:inline">Audio Libros</span>
            <span className="sm:hidden">Audio</span>
          </TabsTrigger>
          <TabsTrigger value="podcasts" className="rounded-lg data-[state=active]:bg-purple-600 data-[state=active]:text-white text-sm font-medium" data-testid="tab-podcasts">
            <i className="fas fa-podcast mr-2"></i>
            <span className="hidden sm:inline">Podcasts</span>
            <span className="sm:hidden">Podcast</span>
          </TabsTrigger>
        </TabsList>

        {/* Tab Videos */}
        <TabsContent value="videos" className="mt-6">
          {loading ? <LoadingSpinner text="Cargando videos..." /> : (
            <ClaseGrid
              clases={clases}
              user={user}
              onClaseClick={handleVerVideos}
              color="red"
              actionLabel={isAdmin || isProfesor ? "Videos Guardados" : "Ver Videos"}
              actionIcon="fab fa-youtube"
            />
          )}
        </TabsContent>

        {/* Tab Audio Libros */}
        <TabsContent value="audiolibros" className="mt-6">
          {selectedClase && selectedTipo === 'audiolibro' ? (
            <MediaPanel clase={selectedClase} tipo="audiolibro" user={user} onBack={handleBackToClases} />
          ) : loading ? (
            <LoadingSpinner text="Cargando clases..." />
          ) : (
            <ClaseGrid
              clases={clases}
              user={user}
              onClaseClick={(clase) => handleMediaClaseClick(clase, 'audiolibro')}
              color="emerald"
              actionLabel="Audio Libros"
              actionIcon="fas fa-headphones"
            />
          )}
        </TabsContent>

        {/* Tab Podcasts */}
        <TabsContent value="podcasts" className="mt-6">
          {selectedClase && selectedTipo === 'podcast' ? (
            <MediaPanel clase={selectedClase} tipo="podcast" user={user} onBack={handleBackToClases} />
          ) : loading ? (
            <LoadingSpinner text="Cargando clases..." />
          ) : (
            <ClaseGrid
              clases={clases}
              user={user}
              onClaseClick={(clase) => handleMediaClaseClick(clase, 'podcast')}
              color="purple"
              actionLabel="Podcasts"
              actionIcon="fas fa-podcast"
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default VideosClases;
